'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, Language, TranslationKeys } from '@/lib/i18n';

interface LanguageContextType {
	language: Language;
	setLanguage: (lang: Language) => void;
	t: TranslationKeys;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
	const [language, setLanguageState] = useState<Language>('fr');

	useEffect(() => {
		// Load saved language from localStorage
		try {
			const savedLang = localStorage.getItem('language') as Language;
			if (savedLang && ['fr', 'en'].includes(savedLang)) {
				setLanguageState(savedLang);
			}
		} catch {
			/* stockage indisponible */
		}
	}, []);

	useEffect(() => {
		document.documentElement.lang = language;
	}, [language]);

	const setLanguage = (lang: Language) => {
		setLanguageState(lang);
		try {
			localStorage.setItem('language', lang);
		} catch {
			/* stockage indisponible */
		}
	};

	const t = translations[language];

	return (
		<LanguageContext.Provider value={{ language, setLanguage, t }}>
			{children}
		</LanguageContext.Provider>
	);
}

export function useLanguage() {
	const context = useContext(LanguageContext);
	if (context === undefined) {
		throw new Error('useLanguage must be used within a LanguageProvider');
	}
	return context;
}
